// Single source of truth for the editorial project hierarchy.
// Lower index = higher priority. Drives:
//   - Homepage hero rotation order
//   - /work gallery default sort
//   - Related projects on detail pages
//   - Personalized recommendations
//
// Edit this list to reshape the entire site's "best work" narrative.

import { slugify } from "./index";

/** Editorial priority, top to bottom. Lower index = stronger / shown first. */
export const PROJECT_PRIORITY_ORDER: { slug: string; name: string }[] = [
  { slug: "grupo-bafar", name: "Grupo Bafar" },
  { slug: "shy-boy",           name: "Shy Boy" },
  { slug: "even-coast",        name: "Even Coast" },
  { slug: "tekmovil",          name: "Tekmovil" },
  { slug: "the-creamery",      name: "The Creamery" },
  { slug: "el-paso-star",      name: "El Paso Star" },
  { slug: "private-penthouse", name: "Private Penthouse" },
  { slug: "media-monks",       name: "Media Monks" },
  { slug: "private-residence", name: "Private Residence" },
  { slug: "rappi",             name: "Rappi" },
  { slug: "telemundo",         name: "Telemundo" },
  { slug: "casa-ms",           name: "Casa MS" },
  { slug: "speak-easy",        name: "Speak Easy" },
  { slug: "birkenstock",       name: "Birkenstock" },
];

const SLUG_RANK = new Map<string, number>(
  PROJECT_PRIORITY_ORDER.map((p, i) => [p.slug, i + 1]),
);
const NAME_RANK = new Map<string, number>(
  PROJECT_PRIORITY_ORDER.map((p, i) => [p.name, i + 1]),
);

/** Rank for a project slug. Lower = higher priority. Unlisted = LAST_RANK. */
export const LAST_RANK = 9999;

export const priorityBySlug = (slug: string): number =>
  SLUG_RANK.get(slug) ?? LAST_RANK;

export const priorityByName = (name: string): number => {
  if (NAME_RANK.has(name)) return NAME_RANK.get(name)!;
  // Fallback: try slugified match (handles "Speak Easy" → "speak-easy" etc).
  return SLUG_RANK.get(slugify(name)) ?? LAST_RANK;
};

export const isPriorityProject = (nameOrSlug: string): boolean =>
  NAME_RANK.has(nameOrSlug) || SLUG_RANK.has(nameOrSlug) || SLUG_RANK.has(slugify(nameOrSlug));

/** Comparator for arrays containing project rows with a `name` field. */
export const byPriorityName = <T extends { name: string }>(a: T, b: T): number =>
  priorityByName(a.name) - priorityByName(b.name);

/* ─── Editorial service-tier ordering (shared by /work list and
 *     the "Explore Similar Work" recommender on project pages) ─── */

/** Full-Ecosystem projects that should sit at the tail of the FE block,
 *  just above Value Analysis. */
export const TAIL_FULL_ECOSYSTEM = new Set<string>([
  "Rothenbucher",
  "San Antonio St.",
  "St. Rogers",
  "Electricity Sign",
  "Weekend Gallery",
]);

type EditorialRow = {
  name: string;
  serviceType: string;
  inProgress?: boolean;
};

/** Lower = more important. Mirrors the /work list view ordering exactly. */
export const servicePriority = (p: EditorialRow): number => {
  if (p.serviceType === "Lighting Design" && !p.inProgress) {
    if (TAIL_FULL_ECOSYSTEM.has(p.name)) return 2; // tail FE block, right before VA
    return 0;                                       // Full Ecosystem completed
  }
  if (p.inProgress) return 1;                       // In-progress right after FE
  if (p.serviceType === "In Progress") return 1;
  if (p.serviceType === "Value Analysis") return 3; // VA at the bottom
  return 4;
};

/** Comparator matching /work list view: editorial priority list
 *  first, then service tier (Full Ecosystem → In Progress → tail FE → VA). */
export const editorialCompare = <T extends EditorialRow>(a: T, b: T): number => {
  const ra = priorityByName(a.name);
  const rb = priorityByName(b.name);
  if (ra !== rb) return ra - rb;
  return servicePriority(a) - servicePriority(b);
};

